'use client'

import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { adminMenu, orgMenu } from '@/constant/sidebar'
import type { Feature, Role } from '@/enum/global.enum'
import { useActiveOrg } from '@/hooks/use-active-org'
import { usePermissions } from '@/hooks/use-permissions'
import { usePinnedSidebar } from '@/hooks/use-pinned-sidebar'
import { isAdminRole, isOrgRole } from '@/utils/role-check.utils'
import { Pin, X, type LucideIcon } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation } from 'react-router'

interface SidebarMenuItem {
  title?: string
  name?: string
  url: string
  icon?: LucideIcon
  role?: Role[]
  feature?: Feature
  items?: SidebarMenuItem[]
}

interface MenuItemInfo {
  title: string
  url: string
  icon?: LucideIcon
  role?: Role[]
  feature?: Feature
}

/**
 * URL to friendly name mapping for better clarity
 * Maps URLs to more descriptive names (e.g., "Dashboard" -> "Policy Dashboard")
 */
const URL_TO_FRIENDLY_NAME: Record<string, string> = {
  // Main Dashboard
  '/': 'Main Dashboard',

  // Policy
  '/policy-dashboard': 'Policy Dashboard',
  '/policy': 'Policy Register',
  '/exemptions-register': 'Exemptions Register',

  // Task Management
  '/tasks-dashboard': 'Tasks Dashboard',
  '/manage-projects': 'Assurance Tasks',

  // Meeting Minutes
  '/meeting-minutes': 'Meeting Minutes',

  // Supply Chain Management
  '/service-provider-dashboard': 'Supply Chain Management Dashboard',
  '/service-provider-list': 'Suppliers',
  '/service-provider-assessment': 'Supply Chain Assessment',
  '/service-provider-report-library': 'Report Library',
  '/assessment-library': 'Questionnaire Library',

  // Staff & Documents
  '/manage-staff': 'Staff Register',
  '/document-repo': 'Document Repository',
  '/country-regulatory-register-oos': 'Regulatory Register',
  '/change-management-process': 'Change Management',

  // Risk
  '/risk-dashboard': 'Risk Dashboard',
  '/risk-list': 'Risk Register',
  '/risk-tracking': 'Risk Tracking',
  '/plan-of-action': 'Plan Of Action',

  // Information Assets
  '/information-asset-report': 'Asset Dashboard',
  '/information-asset': 'Information Assets',

  // Incident
  '/incident-dashboard': 'Incident Dashboard',
  '/incident': 'Incident Register',

  // Compliance
  '/compliance-report': 'Compliance Dashboard',
  '/assessments': 'Standards & Assessments',
  '/report-library': 'Report Library',

  // Settings
  '/settings': 'Settings',
  '/settings/utility': 'Utility Settings',
  '/settings/roles-and-permissions': 'Roles & Permissions',
  '/manage-department': 'Manage Department',
  '/settings/external-integration': 'API Integrations',
  '/settings/jira-integration': 'Jira Integration',

  // Others
  '/manage-users': 'Manage Users',
  '/organisation/resource': 'FAQ',
  '/bug-report': 'Bug Report',
  '/activity-logs/all-users': 'Activity Logs',
  '/login-logs': 'Login Logs',
  '/release-notes': 'Release Notes',

  // Admin
  '/admin/standards': 'Admin Standards',
  '/admin/standard-levels': 'Standard Levels',
  '/admin/saq-types': 'SAQ Types',
  '/admin/fiscal-years': 'Fiscal Years',
  '/admin/control-groups': 'Control Groups',
  '/admin/controls': 'Controls',
  '/admin/control-mapping': 'Control Mapping',
  '/admin/control-questionnaires': 'Control Questionnaires',
  '/admin/maturity-models': 'Maturity Models',
  '/admin/maturity-levels': 'Maturity Levels',
  '/admin/module': 'Module Settings',
  '/admin/organisations': 'Organisations',
  '/admin/organisation-users': 'Organisation Users',
  '/admin/clerk-users': 'Clerk Users',
  '/admin/subscriptions': 'Subscriptions',
  '/admin/subscription-type': 'Subscription Types',
  '/admin/admins': 'Admins List',
  '/admin/signup-request': 'Signup Requests',
  '/admin/resources': 'Manage FAQ',
  '/admin/helpful-tips': 'Helpful Tips',
  '/admin/it-privileged-operations': 'IT Privileged Ops',
  '/admin/release-notes': 'Manage Release Notes',
  '/admin/bug-report': 'Bug Reports',
  '/admin/fcm-tokens': 'FCM Tokens',
}

/**
 * Get friendly name for a URL
 */
function getFriendlyName(url: string, fallbackTitle: string): string {
  return URL_TO_FRIENDLY_NAME[url] || fallbackTitle
}

/**
 * Recursively find a menu item by URL from the sidebar configuration
 */
function findMenuItemByUrl(items: SidebarMenuItem[], url: string): MenuItemInfo | null {
  for (const item of items) {
    if (item.url === url) {
      return {
        title: getFriendlyName(url, item.title || item.name || ''),
        url: item.url,
        icon: item.icon,
        role: item.role,
        feature: item.feature,
      }
    }
    if (item.items && item.items.length > 0) {
      const found = findMenuItemByUrl(item.items, url)
      if (found) return found
    }
  }
  return null
}

/**
 * Get all menu items from sidebar config based on role
 */
function getAllMenuItems(role: Role | undefined): SidebarMenuItem[] {
  if (!role) return []
  if (isAdminRole(role)) return adminMenu.navMain
  if (isOrgRole(role)) return [...orgMenu.navMain, ...orgMenu.others.map((o) => ({ ...o, title: o.title }))]
  return []
}

export function NavPinned() {
  const { pinnedItems, unpinItem, isLoading } = usePinnedSidebar()
  const { canNavigate } = usePermissions()
  const { activeOrg } = useActiveOrg()
  const location = useLocation()

  // Filter pinned items to only show valid ones with icons and friendly names
  const validPinnedItems = useMemo(() => {
    const menuItems = getAllMenuItems(activeOrg?.role as Role)

    return pinnedItems
      .map((pinned) => {
        const menuItem = findMenuItemByUrl(menuItems, pinned.url)
        if (!menuItem) return null

        // Check if user has permission to access this item
        const hasPermission = canNavigate(menuItem.role, menuItem.feature)
        if (!hasPermission) return null

        return {
          url: pinned.url,
          title: menuItem.title, // Use friendly name from mapping
          icon: menuItem.icon,
        }
      })
      .filter(Boolean) as Array<{ title: string; url: string; icon?: LucideIcon }>
  }, [pinnedItems, activeOrg?.role, canNavigate])

  // Don't render if no valid pinned items or loading
  if (isLoading || validPinnedItems.length === 0) return null

  return (
    <SidebarGroup className='-mb-3'>
      <SidebarGroupLabel className='flex items-center gap-1.5 mt-1'>
        <Pin className='h-3 w-3' />
        Pinned
      </SidebarGroupLabel>
      <SidebarMenu>
        {validPinnedItems.map((item) => {
          const Icon = item.icon
          return (
            <SidebarMenuItem key={item.url} className='group/pinned'>
              <SidebarMenuButton asChild isActive={location.pathname === item.url} tooltip={item.title}>
                <Link to={item.url}>
                  {Icon && <Icon className='h-4 w-4' />}
                  <span className='truncate'>{item.title}</span>
                </Link>
              </SidebarMenuButton>

              <SidebarMenuAction
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  unpinItem(item.url)
                }}
                className='opacity-0 group-hover/pinned:opacity-100 transition-opacity cursor-pointer'
                title='Unpin'
              >
                <X className='h-3.5 w-3.5' />
              </SidebarMenuAction>
            </SidebarMenuItem>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
